<template>
  <div>
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-lg font-semibold text-gray-800">HCC Gaps</h2>
    </div>

    <!-- Gap distribution -->
    <div v-if="gapStats" class="bg-white border border-gray-200 rounded-lg p-5 mb-4">
      <div class="flex items-center gap-8">
        <!-- Bucket cards -->
        <div class="flex-1 grid grid-cols-2 md:grid-cols-5 gap-4">
          <div>
            <div class="text-xs text-gray-500 mb-1">Total Gaps</div>
            <div class="text-lg font-semibold text-gray-800">{{ gapStats.totalGaps.toLocaleString() }}</div>
            <div class="text-xs text-gray-400">across {{ gapStats.uniquePatients.toLocaleString() }} patients</div>
          </div>
          <div>
            <div class="text-xs text-gray-500 mb-1">1 Gap</div>
            <div class="text-lg font-semibold text-green-700">{{ gapStats.oneGap.toLocaleString() }}</div>
            <div class="text-xs text-gray-400">{{ gapStats.oneGapPct }}% of patients</div>
          </div>
          <div>
            <div class="text-xs text-gray-500 mb-1">2-3 Gaps</div>
            <div class="text-lg font-semibold text-amber-600">{{ gapStats.twoThreeGaps.toLocaleString() }}</div>
            <div class="text-xs text-gray-400">{{ gapStats.twoThreeGapsPct }}% of patients</div>
          </div>
          <div>
            <div class="text-xs text-gray-500 mb-1">4+ Gaps</div>
            <div class="text-lg font-semibold text-red-600">{{ gapStats.fourPlusGaps.toLocaleString() }}</div>
            <div class="text-xs text-gray-400">{{ gapStats.fourPlusGapsPct }}% of patients</div>
          </div>
          <div>
            <div class="text-xs text-gray-500 mb-1">Unique HCCs</div>
            <div class="text-lg font-semibold text-gray-800">{{ gapStats.uniqueHccs.toLocaleString() }}</div>
            <div class="text-xs text-gray-400">{{ gapStats.avgGaps }} avg gaps/patient</div>
          </div>
        </div>

        <!-- Patient distribution bar -->
        <div class="flex-shrink-0 w-48">
          <div class="text-xs text-gray-500 mb-2">Patient Distribution</div>
          <div class="flex h-6 rounded overflow-hidden">
            <div :style="{ width: gapStats.oneGapPct + '%', backgroundColor: '#16a34a' }" class="transition-all" :title="'1 gap: ' + gapStats.oneGap + ' patients'" />
            <div :style="{ width: gapStats.twoThreeGapsPct + '%', backgroundColor: '#d97706' }" class="transition-all" :title="'2-3 gaps: ' + gapStats.twoThreeGaps + ' patients'" />
            <div :style="{ width: gapStats.fourPlusGapsPct + '%', backgroundColor: '#dc2626' }" class="transition-all" :title="'4+ gaps: ' + gapStats.fourPlusGaps + ' patients'" />
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="py-10 text-center text-sm text-gray-400">Loading…</div>
    <DataTable
      v-else
      :columns="columns"
      :rows="rowData"
      row-key="mbi"
      max-height="600px"
      saved-view-key="ext-hcc-gaps"
      :has-saved="hasSavedView"
      :saved-just-saved="savedViewJustSaved"
      v-model:sort-model="sortModel"
      export-title="HCC Gaps"
      export-filename="cclf_hcc_gaps"
      empty-text="No HCC gap data available."
      @save-view="saveView"
      @reset-view="resetView"
    />
  </div>
</template>

<script>
import { DataTable } from '@/components/External/table'
import savedView from '@/mixins/savedView'

export default {
  name: 'CCLFReport_HCCGaps',
  components: { DataTable },
  mixins: [savedView],
  savedView: {
    key: 'ext-hcc-gaps',
    fields: ['sortModel'],
    scopeProp: 'entityId',
  },
  props: {
    contractId: { type: String, default: null },
    performanceYear: { type: Number, default: null },
    dosthru: { type: String, default: null },
    entityId: { type: Number, default: null },
    careSetting: { type: String, default: '' },
  },
  data() {
    return {
      rowData: [],
      loading: false,
      sortModel: { key: null, dir: 'asc' },
      columns: [
        { field: 'mbi', header: 'Patient MBI', type: 'text', sticky: true, width: 130, filterable: true },
        { field: 'first_name', header: 'Patient First', type: 'text', filterable: true },
        { field: 'last_name', header: 'Patient Last', type: 'text', filterable: true },
        { field: 'dob', header: 'Patient DOB', type: 'date', width: 110 },
        { field: 'patient_facility', header: 'Patient Facility', type: 'text', width: 200, filterable: true },
        { field: 'plurality', header: 'Plurality', type: 'number', width: 90 },
        { field: 'hcc', header: 'HCC', type: 'number', width: 70 },
        { field: 'icd10', header: 'ICD-10', type: 'text', width: 100, filterable: true },
        { field: 'description', header: 'Description', type: 'text', width: 300, filterable: true },
      ],
    }
  },
  computed: {
    gapStats() {
      if (!this.rowData.length) return null
      const totalGaps = this.rowData.length
      const patientGaps = {}
      const hccSet = new Set()
      for (const row of this.rowData) {
        patientGaps[row.mbi] = (patientGaps[row.mbi] || 0) + 1
        if (row.hcc) hccSet.add(row.hcc)
      }
      const patients = Object.values(patientGaps)
      const uniquePatients = patients.length
      const oneGap = patients.filter(c => c === 1).length
      const twoThreeGaps = patients.filter(c => c >= 2 && c <= 3).length
      const fourPlusGaps = patients.filter(c => c >= 4).length
      return {
        totalGaps, uniquePatients, uniqueHccs: hccSet.size,
        avgGaps: (totalGaps / uniquePatients).toFixed(1),
        oneGap, twoThreeGaps, fourPlusGaps,
        oneGapPct: Math.round((oneGap / uniquePatients) * 100),
        twoThreeGapsPct: Math.round((twoThreeGaps / uniquePatients) * 100),
        fourPlusGapsPct: Math.round((fourPlusGaps / uniquePatients) * 100),
      }
    },
  },
  watch: {
    careSetting() { this.loadData() },
    contractId() { this.loadData() },
    performanceYear() { this.loadData() },
    dosthru() { this.loadData() },
    entityId() { this.loadData() },
  },
  mounted() {
    if (this.contractId && this.performanceYear) this.loadData()
  },
  methods: {
    async loadData() {
      if (!this.contractId || !this.performanceYear) return
      this.loading = true
      try {
        const response = await this.$axios.post('/api/portfolio_management/cclf-report/hcc-gaps/', {
          contract_id: this.contractId, performance_year: this.performanceYear,
          dosthru: this.dosthru, entity_id: this.entityId,
          care_setting: this.careSetting || undefined,
        })
        this.rowData = response.data
      } catch (error) {
        console.error('Failed to load HCC gaps:', error)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
